import React, { useState, useEffect, useCallback } from 'react';
import { invSupplierService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtDate, friendlyErr } from './inventoryStyles';

const EMPTY_FORM = { name: '', phone: '', email: '', gstin: '', address: '', contact_person: '', notes: '', is_active: true };

const Suppliers = ({ restaurant }) => {
  const [suppliers, setSuppliers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await invSupplierService.getAll(restaurantId);
    setSuppliers(data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setEditing(null); setForm(EMPTY_FORM); setError(''); setShowForm(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, phone: s.phone || '', email: s.email || '', gstin: s.gstin || '',
              address: s.address || '', contact_person: s.contact_person || '', notes: s.notes || '', is_active: s.is_active });
    setError(''); setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Supplier name is required.'); return; }
    setSaving(true); setError('');
    const { error: e } = await invSupplierService.upsert(restaurantId, { ...form, id: editing?.id });
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false); load();
  };

  const toggle = async (s) => {
    await invSupplierService.setActive(s.id, !s.is_active);
    load();
  };

  const filtered = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.phone || '').includes(search) ||
    (s.contact_person || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Suppliers</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>{suppliers.filter(s => s.is_active).length} active suppliers</p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ Add Supplier</button>
        </div>

        <div style={{ ...card, padding: '14px 20px', marginBottom: '20px' }}>
          <input placeholder="Search suppliers…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle(), width: '280px', padding: '8px 12px' }} />
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No suppliers yet</div>
              <div>Add your first supplier to link with purchase orders.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                  {['Supplier', 'Contact', 'Phone', 'GSTIN', 'Added', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: s.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '700', color: '#333' }}>{s.name}</div>
                      {s.address && <div style={{ fontSize: '0.75rem', color: '#888' }}>{s.address}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{s.contact_person || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{s.phone || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#555', fontFamily: 'monospace', fontSize: '0.82rem' }}>{s.gstin || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{fmtDate(s.created_at)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                        background: s.is_active ? '#f0fdf4' : '#f8fafc', color: s.is_active ? '#16a34a' : '#64748b',
                        border: `1px solid ${s.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={{ ...btn('outline'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => openEdit(s)}>Edit</button>
                        <button style={{ ...btn(s.is_active ? 'danger' : 'green'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => toggle(s)}>
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={modalBox}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>{editing ? 'Edit Supplier' : 'New Supplier'}</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Supplier Name *</label>
                <input style={inputStyle(!form.name.trim())} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Fresh Farm Produce" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Contact Person</label>
                <input style={inputStyle()} value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Phone</label>
                <input style={inputStyle()} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} type="tel" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Email</label>
                <input style={inputStyle()} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>GSTIN</label>
                <input style={inputStyle()} value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} placeholder="Optional" />
              </div>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Address</label>
                <textarea style={{ ...inputStyle(), height: '72px', resize: 'vertical' }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Optional" />
              </div>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Notes</label>
                <input style={inputStyle()} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Supplier'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
